import { Component, useState, useEffect, useMemo } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { useLocation, useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ChevronLeft, AlertTriangle, CheckCircle2, XCircle, Clock } from "lucide-react";

const SECRET_STORAGE_KEY = "pa_admin_secret";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface CategoryResult {
  key: string;
  title: string;
  status: "scored" | "stub" | "not_yet_defined" | "error";
  score: number | null;
  rationale: string;
  automaticFails: string[];
  weight?: number;
}

interface EvaluationRow {
  id: number;
  jobId: number;
  userId: string | null;
  mode: string | null;
  rubricVersion: string;
  judgeModel: string;
  promptHash: string;
  categories: CategoryResult[];
  automaticFails: string[];
  overallVerdict: "pass" | "fail" | "incomplete" | "needs_review" | "regenerate_recommended";
  overallScore: number | null;
  minAcceptableScore: number;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  selfBenchmarkText: string | null;
  selfBenchmarkCategories: CategoryResult[] | null;
  selfBenchmarkVerdict: string | null;
  selfBenchmarkScore: number | null;
  selfBenchmarkError: string | null;
  selfBenchmarkDurationMs: number | null;
}

interface ListResponse {
  rubricVersion: string;
  judgeModel: string;
  promptHash: string;
  evaluations: EvaluationRow[];
}

interface JobMeta {
  id: number;
  userId: string | null;
  mode: string | null;
  status: string;
  originalFilename: string;
  targetPercent: number;
  compressionRatio: number | null;
  outputDurationSeconds: number | null;
  originalDurationSeconds: number | null;
  createdAt: string;
}

interface DetailResponse {
  job: JobMeta;
  transcriptSegments: Array<{ segment_id: number; text: string; speaker?: string }>;
  keyQuoteSegments: Array<{ segment_id: number; text: string; speaker?: string }>;
  transcriptSource: "job" | "evaluation_snapshot" | "mixed" | "unavailable";
  evaluations: EvaluationRow[];
}

type SortKey = "createdAt" | "jobId" | "verdict" | "overallScore" | "autoFails";
type SortDir = "asc" | "desc";

const REFINEMENT_DAYS_OPTIONS = [7, 14, 30, 90] as const;
type RefinementDays = (typeof REFINEMENT_DAYS_OPTIONS)[number];

interface RefinementStats {
  days: number;
  since: string;
  totalGeminiJobs: number;
  jobsWithAnyAdjustment: number;
  percentAdjusted: number | null;
  meanSegmentsAdjusted: number | null;
  meanSegmentsAdjustedWhenNonZero: number | null;
  maxSegmentsAdjusted: number | null;
  meanStartOffsetMs: number | null;
  maxStartOffsetMs: number | null;
  meanEndOffsetMs: number | null;
  maxEndOffsetMs: number | null;
}

function RefinementStatsPanel({ secret, onLogout }: { secret: string; onLogout: () => void; }) {
  const [days, setDays] = useState<RefinementDays>(30);
  const [stats, setStats] = useState<RefinementStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load(d: RefinementDays) {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(secret, `/admin/refinement-stats?days=${d}`);
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? `Server returned ${res.status}`);
        setStats(null);
        return;
      }
      setStats(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  function fmt(n: number | null, decimals = 2): string {
    if (n === null) return "—";
    return Number.isInteger(n) ? String(n) : n.toFixed(decimals);
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-sm font-mono">Gemini Refinement Stats</h3>
            <p className="text-[10px] uppercase font-mono text-muted-foreground mt-1">
              timestamp boundary refinement pass · Gemini jobs only
            </p>
          </div>
          <div className="flex gap-1">
            {REFINEMENT_DAYS_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2 py-1 text-xs font-mono rounded border transition-colors ${
                  days === d
                    ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-400"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">
            <Loader2 className="inline mr-2 h-3 w-3 animate-spin" />Loading…
          </p>
        )}
        {error && <p className="text-xs text-red-400">{error}</p>}

        {!loading && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Stat label="Gemini jobs" value={String(stats.totalGeminiJobs)} />
              <Stat
                label="With adjustment"
                value={String(stats.jobsWithAnyAdjustment)}
                tone={stats.jobsWithAnyAdjustment > 0 ? "emerald" : undefined}
              />
              <Stat
                label="% adjusted"
                value={stats.percentAdjusted !== null ? `${stats.percentAdjusted}%` : "—"}
              />
              <Stat
                label="Mean segs adj."
                value={fmt(stats.meanSegmentsAdjusted)}
              />
              <Stat
                label="Max segs adj."
                value={fmt(stats.maxSegmentsAdjusted, 0)}
              />
            </div>
            <div>
              <p className="text-[10px] uppercase font-mono text-muted-foreground mb-2">Boundary offsets (ms, adjusted jobs only)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Mean start offset" value={stats.meanStartOffsetMs !== null ? `${fmt(stats.meanStartOffsetMs, 1)} ms` : "—"} />
                <Stat label="Max start offset" value={stats.maxStartOffsetMs !== null ? `${fmt(stats.maxStartOffsetMs, 0)} ms` : "—"} />
                <Stat label="Mean end offset" value={stats.meanEndOffsetMs !== null ? `${fmt(stats.meanEndOffsetMs, 1)} ms` : "—"} />
                <Stat label="Max end offset" value={stats.maxEndOffsetMs !== null ? `${fmt(stats.maxEndOffsetMs, 0)} ms` : "—"} />
              </div>
            </div>
          </>
        )}

        {!loading && stats && (
          <p className="text-[10px] font-mono text-muted-foreground">
            Since {new Date(stats.since).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </p>
        )}

        <div className="border-t border-border pt-4">
          <h4 className="font-bold text-sm font-mono mb-1">Refinement Trend</h4>
          <RefinementTrendsPanel secret={secret} days={days} onLogout={onLogout} />
        </div>
      </CardContent>
    </Card>
  );
}

interface RefinementTrendBucket {
  day: string;
  totalGeminiJobs: number;
  jobsWithAnyAdjustment: number;
  percentAdjusted: number | null;
  meanSegmentsAdjusted: number | null;
}

interface RefinementTrendsResponse {
  days: number;
  since: string;
  buckets: RefinementTrendBucket[];
}

function RefinementTrendsPanel({ secret, days, onLogout }: { secret: string; days: RefinementDays; onLogout: () => void }) {
  const [data, setData] = useState<RefinementTrendsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminFetch(secret, `/admin/refinement-trends?days=${days}`)
      .then(async (res) => {
        if (res.status === 401) { onLogout(); return; }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error ?? `Server returned ${res.status}`);
          setData(null);
          return;
        }
        setData(await res.json());
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const buckets = data?.buckets ?? [];
  const maxJobs = Math.max(1, ...buckets.map((b) => b.totalGeminiJobs));

  function dayLabel(iso: string) {
    const [, mm, dd] = iso.split("-");
    return `${Number(mm)}/${Number(dd)}`;
  }

  return (
    <div className="space-y-2">
      <div>
        <p className="text-[10px] uppercase font-mono text-muted-foreground mt-1">
          refinement usage · daily buckets
        </p>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="inline mr-2 h-3 w-3 animate-spin" />Loading…
        </p>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}

      {!loading && buckets.length === 0 && !error && (
        <p className="text-xs text-muted-foreground">No Gemini jobs in this window.</p>
      )}

      {!loading && buckets.length > 0 && (
        <div className="overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-2 py-1">Day</th>
                <th className="text-left px-2 py-1">Gemini jobs</th>
                <th className="text-left px-2 py-1 w-1/3">% adjusted</th>
                <th className="text-right px-2 py-1">Mean segs adj.</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.day} className="border-t border-border">
                  <td className="px-2 py-1">{dayLabel(b.day)}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden w-20">
                        <div
                          className="h-full bg-cyan-400/60"
                          style={{ width: `${(b.totalGeminiJobs / maxJobs) * 100}%` }}
                        />
                      </div>
                      <span>{b.totalGeminiJobs}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    {b.percentAdjusted !== null ? (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden flex-1 max-w-[160px]">
                          <div
                            className="h-full bg-violet-400/70"
                            style={{ width: `${b.percentAdjusted}%` }}
                          />
                        </div>
                        <span>{b.percentAdjusted}%</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {b.meanSegmentsAdjusted !== null ? b.meanSegmentsAdjusted : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface TrendBucket {
  label: string;
  count: number;
  passRate: number | null;
  meanScore: number | null;
}

function startOfDayUTC(iso: string): number {
  const d = new Date(iso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function computeTrends(rows: EvaluationRow[], days = 14): TrendBucket[] {
  if (rows.length === 0) return [];
  const buckets = new Map<number, EvaluationRow[]>();
  for (const r of rows) {
    const key = startOfDayUTC(r.createdAt);
    const arr = buckets.get(key) ?? [];
    arr.push(r);
    buckets.set(key, arr);
  }
  const sortedKeys = Array.from(buckets.keys()).sort((a, b) => b - a).slice(0, days);
  return sortedKeys
    .sort((a, b) => a - b)
    .map((k) => {
      const arr = buckets.get(k)!;
      const passes = arr.filter((r) => r.overallVerdict === "pass").length;
      const decided = arr.filter((r) => r.overallVerdict !== "incomplete").length;
      const scored = arr.filter((r) => typeof r.overallScore === "number");
      const meanScore = scored.length > 0
        ? scored.reduce((s, r) => s + (r.overallScore ?? 0), 0) / scored.length
        : null;
      const passRate = decided > 0 ? passes / decided : null;
      const d = new Date(k);
      const label = `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      return { label, count: arr.length, passRate, meanScore };
    });
}

function getStoredSecret(): string {
  return sessionStorage.getItem(SECRET_STORAGE_KEY) ?? "";
}

function setStoredSecret(secret: string): void {
  if (secret) sessionStorage.setItem(SECRET_STORAGE_KEY, secret);
  else sessionStorage.removeItem(SECRET_STORAGE_KEY);
}

async function adminFetch(secret: string, path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("x-admin-secret", secret);
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  return fetch(`${basePath}/api${path}`, { ...init, headers });
}

function VerdictBadge({ verdict }: { verdict: EvaluationRow["overallVerdict"] }) {
  if (verdict === "pass") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 font-mono text-[10px] uppercase">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Pass
      </Badge>
    );
  }
  if (verdict === "fail") {
    return (
      <Badge className="bg-red-500/15 text-red-400 border-red-500/30 font-mono text-[10px] uppercase">
        <XCircle className="mr-1 h-3 w-3" /> Fail
      </Badge>
    );
  }
  if (verdict === "needs_review") {
    return (
      <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[10px] uppercase">
        <AlertTriangle className="mr-1 h-3 w-3" /> Needs Review
      </Badge>
    );
  }
  if (verdict === "regenerate_recommended") {
    return (
      <Badge className="bg-orange-500/15 text-orange-400 border-orange-500/30 font-mono text-[10px] uppercase">
        <RefreshCw className="mr-1 h-3 w-3" /> Regen Recommended
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[10px] uppercase">
      <AlertTriangle className="mr-1 h-3 w-3" /> Incomplete
    </Badge>
  );
}

function SecretForm({ onSubmit, error }: { onSubmit: (s: string) => void; error?: string }) {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 space-y-4">
          <div>
            <h1 className="text-xl font-bold font-mono">Admin: Quality Evaluations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Enter the admin secret to view internal rubric scores. The secret is held only in this browser tab.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (value.trim()) onSubmit(value.trim());
            }}
            className="space-y-3"
          >
            <div>
              <Label htmlFor="admin-secret" className="text-xs">Admin secret</Label>
              <Input
                id="admin-secret"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
                className="font-mono"
              />
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <Button type="submit" className="w-full">Continue</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

interface AggregateStats {
  total: number;
  passed: number;
  failed: number;
  incomplete: number;
  meanScore: number | null;
  byCategory: Map<string, { title: string; mean: number | null; count: number }>;
}

function computeAggregates(rows: EvaluationRow[]): AggregateStats {
  const byCategory = new Map<string, { title: string; sum: number; count: number }>();
  let scoreSum = 0;
  let scoreCount = 0;
  let passed = 0, failed = 0, incomplete = 0;
  for (const r of rows) {
    if (r.overallVerdict === "pass") passed++;
    else if (r.overallVerdict === "fail") failed++;
    else incomplete++;
    if (typeof r.overallScore === "number") {
      scoreSum += r.overallScore;
      scoreCount++;
    }
    for (const c of (Array.isArray(r.categories) ? r.categories : [])) {
      if (c.status === "scored" && typeof c.score === "number") {
        const prev = byCategory.get(c.key) ?? { title: c.title, sum: 0, count: 0 };
        prev.sum += c.score;
        prev.count++;
        byCategory.set(c.key, prev);
      }
    }
  }
  const byCategoryOut = new Map<string, { title: string; mean: number | null; count: number }>();
  for (const [k, v] of byCategory) {
    byCategoryOut.set(k, { title: v.title, mean: v.count > 0 ? v.sum / v.count : null, count: v.count });
  }
  return {
    total: rows.length,
    passed, failed, incomplete,
    meanScore: scoreCount > 0 ? scoreSum / scoreCount : null,
    byCategory: byCategoryOut,
  };
}

function ListView({ secret, onPickJob, onLogout }: { secret: string; onPickJob: (jobId: number) => void; onLogout: () => void }) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterJobId, setFilterJobId] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function toggleSort(k: SortKey) {
    if (k === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterMode) params.set("mode", filterMode);
      if (filterUser) params.set("userId", filterUser);
      if (filterJobId) params.set("jobId", filterJobId);
      const res = await adminFetch(secret, `/admin/evaluations?${params}`);
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) {
        setError(`Server returned ${res.status}`);
        setData(null);
        return;
      }
      setData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aggregates = useMemo(() => data ? computeAggregates(data.evaluations) : null, [data]);
  const trends = useMemo(() => data ? computeTrends(data.evaluations) : [], [data]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const dir = sortDir === "asc" ? 1 : -1;
    const verdictOrder: Record<EvaluationRow["overallVerdict"], number> = { pass: 4, needs_review: 3, incomplete: 2, regenerate_recommended: 1, fail: 0 };
    return [...data.evaluations].sort((a, b) => {
      switch (sortKey) {
        case "createdAt":
          return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
        case "jobId":
          return (a.jobId - b.jobId) * dir;
        case "verdict":
          return (verdictOrder[a.overallVerdict] - verdictOrder[b.overallVerdict]) * dir;
        case "overallScore":
          return ((a.overallScore ?? -Infinity) - (b.overallScore ?? -Infinity)) * dir;
        case "autoFails":
          return ((Array.isArray(a.automaticFails) ? a.automaticFails.length : 0) - (Array.isArray(b.automaticFails) ? b.automaticFails.length : 0)) * dir;
      }
    });
  }, [data, sortKey, sortDir]);

  return (
    <div className="min-h-[100dvh] bg-background p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-mono">Quality Evaluations</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            Internal admin · rubric {data?.rubricVersion ?? "—"} · model {data?.judgeModel ?? "—"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button size="sm" variant="outline" onClick={onLogout}>Sign out</Button>
        </div>
      </div>

      <TrendsPanel trends={trends} />

      <RefinementStatsPanel secret={secret} onLogout={onLogout} />

      {aggregates && (
        <Card>
          <CardContent className="pt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat label="Total" value={String(aggregates.total)} />
            <Stat label="Passed" value={String(aggregates.passed)} tone="emerald" />
            <Stat label="Failed" value={String(aggregates.failed)} tone="red" />
            <Stat label="Incomplete" value={String(aggregates.incomplete)} tone="amber" />
            <Stat label="Mean overall" value={aggregates.meanScore !== null ? aggregates.meanScore.toFixed(2) : "—"} />
            <div className="col-span-full">
              <p className="text-[10px] uppercase font-mono text-muted-foreground mb-2">Mean per category (scored only)</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Array.from(aggregates.byCategory.entries()).map(([k, v]) => (
                  <div key={k} className="text-xs flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2">
                    <span className="truncate">{v.title}</span>
                    <span className="font-mono shrink-0">{v.mean !== null ? v.mean.toFixed(2) : "—"} <span className="text-muted-foreground">({v.count})</span></span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Mode</Label>
              <Input value={filterMode} onChange={(e) => setFilterMode(e.target.value)} placeholder="quick_catchup, key_quotes, full_recap" className="font-mono" />
            </div>
            <div>
              <Label className="text-xs">User ID</Label>
              <Input value={filterUser} onChange={(e) => setFilterUser(e.target.value)} placeholder="user_…" className="font-mono" />
            </div>
            <div>
              <Label className="text-xs">Job ID</Label>
              <Input value={filterJobId} onChange={(e) => setFilterJobId(e.target.value)} placeholder="123" className="font-mono" />
            </div>
            <div className="flex items-end">
              <Button onClick={() => void load()} disabled={loading} className="w-full">Apply</Button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="overflow-auto border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] uppercase font-mono text-muted-foreground">
                <tr>
                  <SortableTh label="Created" k="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Job" k="jobId" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-left px-3 py-2">Mode</th>
                  <th className="text-left px-3 py-2">User</th>
                  <SortableTh label="Verdict" k="verdict" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Overall" k="overallScore" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-left px-3 py-2">Categories</th>
                  <SortableTh label="Auto-fails" k="autoFails" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-left px-3 py-2">Rubric</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground"><Loader2 className="inline mr-2 h-4 w-4 animate-spin" /> Loading…</td></tr>
                )}
                {!loading && (Array.isArray(data?.evaluations) && data.evaluations.length === 0) && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No evaluations match.</td></tr>
                )}
                {!loading && sortedRows.map((e) => (
                  <tr key={e.id} onClick={() => onPickJob(e.jobId)} className="border-t border-border cursor-pointer hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono text-xs">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono">#{e.jobId}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.mode ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs truncate max-w-[140px]">{e.userId ?? "—"}</td>
                    <td className="px-3 py-2"><VerdictBadge verdict={e.overallVerdict} /></td>
                    <td className="px-3 py-2 font-mono">{e.overallScore !== null ? e.overallScore.toFixed(2) : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {Array.isArray(e.categories) ? e.categories.map((c) => (
                        <span key={c.key} className="inline-block mr-2" title={c.title}>
                          {c.score !== null ? c.score : "·"}
                        </span>
                      )) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{Array.isArray(e.automaticFails) && e.automaticFails.length > 0 ? <span className="text-red-400">{e.automaticFails.length}</span> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{e.rubricVersion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CategoryScoreCard({ category: c }: { category: CategoryResult }) {
  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="font-bold text-sm">{c.title}</p>
        <div className="flex items-center gap-2">
          {c.weight !== undefined && c.weight !== 1 && <span className="text-[10px] font-mono text-muted-foreground">×{c.weight}</span>}
          <span className="font-mono text-sm">
            {c.status === "scored" ? `${c.score}/5` : c.status === "stub" ? "stub" : c.status === "not_yet_defined" ? "not yet defined" : "error"}
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{c.rationale}</p>
      {Array.isArray(c.automaticFails) && c.automaticFails.length > 0 && (
        <div className="mt-2 text-xs text-red-400 font-mono">
          {c.automaticFails.map((f, i) => <div key={i}>⚠ {f}</div>)}
        </div>
      )}
    </div>
  );
}

function SortableTh({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      onClick={() => onSort(k)}
      className={`text-left px-3 py-2 cursor-pointer select-none hover:text-foreground ${active ? "text-foreground" : ""}`}
      data-testid={`sort-${k}`}
    >
      {label} <span className="text-[8px] opacity-60">{arrow}</span>
    </th>
  );
}

function TrendsPanel({ trends }: { trends: TrendBucket[] }) {
  if (trends.length === 0) return null;
  const maxCount = Math.max(1, ...trends.map((t) => t.count));
  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <div>
          <h3 className="font-bold text-sm">Trend over time (UTC days, last {trends.length})</h3>
          <p className="text-[10px] uppercase font-mono text-muted-foreground mt-1">
            pass rate and mean overall score, bucketed per day
          </p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-2 py-1">Day</th>
                <th className="text-left px-2 py-1">Volume</th>
                <th className="text-left px-2 py-1 w-1/2">Pass rate</th>
                <th className="text-right px-2 py-1">Mean</th>
              </tr>
            </thead>
            <tbody>
              {trends.map((t) => (
                <tr key={t.label} className="border-t border-border">
                  <td className="px-2 py-1">{t.label}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden w-20">
                        <div
                          className="h-full bg-cyan-400/60"
                          style={{ width: `${(t.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span>{t.count}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    {t.passRate !== null ? (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden flex-1 max-w-[160px]">
                          <div
                            className={`h-full ${t.passRate >= 0.8 ? "bg-emerald-400/70" : t.passRate >= 0.5 ? "bg-amber-400/70" : "bg-red-400/70"}`}
                            style={{ width: `${t.passRate * 100}%` }}
                          />
                        </div>
                        <span>{(t.passRate * 100).toFixed(0)}%</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {t.meanScore !== null ? t.meanScore.toFixed(2) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "emerald" | "red" | "amber" }) {
  const toneClass = tone === "emerald" ? "text-emerald-400" : tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-foreground";
  return (
    <div>
      <p className="text-[10px] uppercase font-mono text-muted-foreground">{label}</p>
      <p className={`text-2xl font-mono font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function DetailView({ secret, jobId, onBack, onLogout }: { secret: string; jobId: number; onBack: () => void; onLogout: () => void }) {
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(secret, `/admin/evaluations/${jobId}`);
      if (res.status === 401) { onLogout(); return; }
      if (res.status === 404) { setError("Job not found"); setData(null); return; }
      if (!res.ok) { setError(`Server returned ${res.status}`); return; }
      setData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function rerun() {
    setRerunning(true);
    try {
      const res = await adminFetch(secret, `/admin/evaluations/${jobId}/rerun`, { method: "POST", body: JSON.stringify({}) });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? `Re-run returned ${res.status}`);
        return;
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRerunning(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  return (
    <div className="min-h-[100dvh] bg-background p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onBack}><ChevronLeft className="mr-1 h-3 w-3" /> Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void rerun()} disabled={rerunning || loading}>
            <RefreshCw className={`mr-2 h-3 w-3 ${rerunning ? "animate-spin" : ""}`} /> Re-run evaluation
          </Button>
        </div>
      </div>

      {loading && <p className="text-muted-foreground"><Loader2 className="inline mr-2 h-4 w-4 animate-spin" /> Loading…</p>}
      {error && <p className="text-red-400">{error}</p>}

      {data && (
        <>
          <Card>
            <CardContent className="pt-6">
              <h2 className="font-bold text-lg mb-2">Job #{data.job.id}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                <div><span className="text-muted-foreground">File:</span> <span className="break-all">{data.job.originalFilename}</span></div>
                <div><span className="text-muted-foreground">Mode:</span> {data.job.mode}</div>
                <div><span className="text-muted-foreground">Status:</span> {data.job.status}</div>
                <div><span className="text-muted-foreground">Target:</span> {data.job.targetPercent}%</div>
                <div><span className="text-muted-foreground">Original:</span> {data.job.originalDurationSeconds?.toFixed(0)}s</div>
                <div><span className="text-muted-foreground">Output:</span> {data.job.outputDurationSeconds?.toFixed(0)}s</div>
                <div><span className="text-muted-foreground">Compression:</span> {data.job.compressionRatio !== null ? (data.job.compressionRatio * 100).toFixed(1) + "%" : "—"}</div>
                <div><span className="text-muted-foreground">User:</span> {data.job.userId ?? "—"}</div>
              </div>
            </CardContent>
          </Card>

          {(Array.isArray(data.evaluations) ? data.evaluations : []).length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                No evaluation has been recorded for this job yet. Use “Re-run evaluation” to score it now.
              </CardContent>
            </Card>
          )}

          {(Array.isArray(data.evaluations) ? data.evaluations : []).map((evalRow) => (
            <Card key={evalRow.id}>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <VerdictBadge verdict={evalRow.overallVerdict} />
                      <span className="font-mono text-sm">Overall: {evalRow.overallScore !== null ? evalRow.overallScore.toFixed(2) : "—"} / 5</span>
                      <span className="text-xs text-muted-foreground font-mono flex items-center gap-1"><Clock className="h-3 w-3" />{evalRow.durationMs ?? "—"}ms</span>
                    </div>
                    <p className="text-[10px] uppercase font-mono text-muted-foreground mt-1">
                      rubric {evalRow.rubricVersion} · {evalRow.judgeModel} · {new Date(evalRow.createdAt).toLocaleString()} · min acceptable {evalRow.minAcceptableScore}
                    </p>
                  </div>
                </div>

                {evalRow.errorMessage && (
                  <p className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2">{evalRow.errorMessage}</p>
                )}

                {evalRow.selfBenchmarkCategories ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[10px] uppercase font-mono text-muted-foreground font-bold">Pipeline output (T₁)</p>
                      {(Array.isArray(evalRow.categories) ? evalRow.categories : []).map((c) => (
                        <CategoryScoreCard key={c.key} category={c} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <p className="text-[10px] uppercase font-mono text-muted-foreground font-bold">Reviewer's best attempt (T_ref)</p>
                        {evalRow.selfBenchmarkVerdict && <VerdictBadge verdict={evalRow.selfBenchmarkVerdict as EvaluationRow["overallVerdict"]} />}
                        {evalRow.selfBenchmarkScore !== null && evalRow.selfBenchmarkScore !== undefined && (
                          <span className="font-mono text-xs text-muted-foreground">{evalRow.selfBenchmarkScore.toFixed(2)} / 5</span>
                        )}
                        {evalRow.selfBenchmarkDurationMs !== null && evalRow.selfBenchmarkDurationMs !== undefined && (
                          <span className="text-xs text-muted-foreground font-mono flex items-center gap-1"><Clock className="h-3 w-3" />{evalRow.selfBenchmarkDurationMs}ms</span>
                        )}
                      </div>
                      {(Array.isArray(evalRow.selfBenchmarkCategories) ? evalRow.selfBenchmarkCategories : []).map((c) => (
                        <CategoryScoreCard key={c.key} category={c} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(Array.isArray(evalRow.categories) ? evalRow.categories : []).map((c) => (
                      <CategoryScoreCard key={c.key} category={c} />
                    ))}
                    {evalRow.selfBenchmarkError ? (
                      <p className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2">
                        Self-benchmark failed: {evalRow.selfBenchmarkError}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Self-benchmark pending or not yet available.</p>
                    )}
                  </div>
                )}

                {evalRow.selfBenchmarkText && (
                  <div>
                    <p className="text-[10px] uppercase font-mono text-muted-foreground mb-2">Reviewer's best attempt transcript (T_ref)</p>
                    <div className="border border-border rounded-md p-3 max-h-48 overflow-auto text-xs text-muted-foreground whitespace-pre-wrap">
                      {evalRow.selfBenchmarkText}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {((data.transcriptSegments ?? []).length > 0 || (data.keyQuoteSegments ?? []).length > 0) && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <h3 className="font-bold">T₀ ↔ T_extracted (side by side)</h3>
                  {data.transcriptSource === "evaluation_snapshot" && (
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[10px] uppercase">
                      Showing stored snapshot from latest evaluation
                    </Badge>
                  )}
                  {data.transcriptSource === "mixed" && (
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[10px] uppercase">
                      Mixed source — one side live, the other from snapshot
                    </Badge>
                  )}
                  {data.transcriptSource === "unavailable" && (
                    <Badge className="bg-red-500/15 text-red-400 border-red-500/30 font-mono text-[10px] uppercase">
                      Transcript not available
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="border border-border rounded-md p-3 max-h-[60vh] overflow-auto">
                    <p className="text-[10px] uppercase font-mono text-muted-foreground mb-2">T₀ — original transcript ({(data.transcriptSegments ?? []).length} segments)</p>
                    {(data.transcriptSegments ?? []).length === 0 ? (
                      <p className="text-muted-foreground italic">No transcript stored on this job and no evaluation snapshot exists yet. Run an evaluation to capture one.</p>
                    ) : (
                      (data.transcriptSegments ?? []).map((s) => (
                        <p key={s.segment_id} className="mb-1"><span className="text-muted-foreground font-mono">[{s.segment_id}]</span> {s.text}</p>
                      ))
                    )}
                  </div>
                  <div className="border border-border rounded-md p-3 max-h-[60vh] overflow-auto">
                    <p className="text-[10px] uppercase font-mono text-muted-foreground mb-2">T_extracted — kept selection ({(data.keyQuoteSegments ?? []).length} segments)</p>
                    {(data.keyQuoteSegments ?? []).length === 0 ? (
                      <p className="text-muted-foreground italic">No extracted segments on this job.</p>
                    ) : (
                      (data.keyQuoteSegments ?? []).map((s) => (
                        <p key={s.segment_id} className="mb-1"><span className="text-muted-foreground font-mono">[{s.segment_id}]</span> {s.text}</p>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function AdminEvaluationsPage() {
  const [secret, setSecret] = useState<string>(() => getStoredSecret());
  const [authError, setAuthError] = useState<string | undefined>();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ jobId: string }>("/admin/evaluations/:jobId");
  const urlJobId = match && params?.jobId && /^\d+$/.test(params.jobId)
    ? Number(params.jobId)
    : null;

  useEffect(() => {
    if (!secret) return;
    // Validate by hitting the list endpoint with limit=1
    void (async () => {
      try {
        const res = await adminFetch(secret, "/admin/evaluations?limit=1");
        if (res.status === 401) {
          setStoredSecret("");
          setSecret("");
          setAuthError("That secret was rejected.");
        } else if (res.status === 503) {
          setAuthError("ADMIN_SECRET is not configured on the server.");
        } else {
          setAuthError(undefined);
        }
      } catch {
        // Ignore network errors; the list view will show its own error.
      }
    })();
  }, [secret]);

  if (!secret) {
    return (
      <SecretForm
        error={authError}
        onSubmit={(s) => {
          setStoredSecret(s);
          setSecret(s);
        }}
      />
    );
  }

  if (urlJobId !== null) {
    return (
      <DetailView
        secret={secret}
        jobId={urlJobId}
        onBack={() => navigate("/admin/evaluations")}
        onLogout={() => {
          setStoredSecret("");
          setSecret("");
        }}
      />
    );
  }

  return (
    <ListView
      secret={secret}
      onPickJob={(j) => navigate(`/admin/evaluations/${j}`)}
      onLogout={() => {
        setStoredSecret("");
        setSecret("");
      }}
    />
  );
}

interface ErrorBoundaryState {
  error: Error | null;
}

class AdminEvaluationsErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AdminEvaluations] render error:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-[100dvh] bg-background flex items-center justify-center p-6">
          <Card className="max-w-lg w-full">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 text-red-400">
                <XCircle className="h-5 w-5 shrink-0" />
                <h2 className="font-bold font-mono text-base">Admin page crashed</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                An unexpected rendering error occurred. Details below.
              </p>
              <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-48 text-red-300 whitespace-pre-wrap break-all">
                {this.state.error.message}
                {"\n\n"}
                {this.state.error.stack}
              </pre>
              <Button
                variant="outline"
                onClick={() => {
                  this.setState({ error: null });
                  window.location.reload();
                }}
              >
                Reload page
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminEvaluationsPageWithBoundary() {
  return (
    <AdminEvaluationsErrorBoundary>
      <AdminEvaluationsPage />
    </AdminEvaluationsErrorBoundary>
  );
}
